import React, { useState, useEffect, useContext } from 'react';
import { Users, Search, RefreshCw, Download, MessageSquare, Phone, Mail, Calendar, ChevronDown, ChevronUp, StickyNote, Flame, Clock, X, Plus, Filter } from 'lucide-react';
import AuthContext from '../context/AuthContext';

const STATUSES = [
  { value: 'pendiente', label: 'Pendiente', color: 'bg-yellow-500/20 text-yellow-400', dot: 'bg-yellow-400' },
  { value: 'contactado', label: 'Contactado', color: 'bg-blue-500/20 text-blue-400', dot: 'bg-blue-400' },
  { value: 'en_conversacion', label: 'En Conversacion', color: 'bg-purple-500/20 text-purple-400', dot: 'bg-purple-400' },
  { value: 're_contactado', label: 'Re-contactado', color: 'bg-cyan-500/20 text-cyan-400', dot: 'bg-cyan-400' },
  { value: 'cotizacion_enviada', label: 'Cotizacion Enviada', color: 'bg-indigo-500/20 text-indigo-400', dot: 'bg-indigo-400' },
  { value: 'cerrado_ganado', label: 'Cerrado (Ganado)', color: 'bg-emerald-500/20 text-emerald-400', dot: 'bg-emerald-400' },
  { value: 'no_interesado', label: 'Ya no le interesa', color: 'bg-red-500/20 text-red-400', dot: 'bg-red-400' },
];

const PRIORITIES = [
  { value: 'alta', label: 'Alta', color: 'text-red-400', icon: '!!', bg: 'bg-red-500/20' },
  { value: 'media', label: 'Media', color: 'text-yellow-400', icon: '!', bg: 'bg-yellow-500/20' },
  { value: 'baja', label: 'Baja', color: 'text-zinc-400', icon: '-', bg: 'bg-zinc-500/20' },
];

const SOURCE_TYPES = [
  { value: 'all', label: 'Todos' },
  { value: 'residencial', label: 'Residencial', color: 'bg-blue-600' },
  { value: 'comercial', label: 'Comercial', color: 'bg-purple-600' },
  { value: 'industrial', label: 'Industrial', color: 'bg-orange-600' },
  { value: 'newsletter', label: 'Newsletter', color: 'bg-emerald-600' },
  { value: 'calculadora', label: 'Calculadora', color: 'bg-yellow-600' },
];

const LeadsPage = () => {
  const { fetchWithAuth } = useContext(AuthContext);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [expandedLead, setExpandedLead] = useState(null);
  const [noteText, setNoteText] = useState('');
  const [showStatusMenu, setShowStatusMenu] = useState(null);
  const [showPriorityMenu, setShowPriorityMenu] = useState(null);
  const [sortBy, setSortBy] = useState('newest');

  // Cerrar menús al hacer click fuera
  useEffect(() => {
    const handleClickOutside = () => {
      setShowStatusMenu(null);
      setShowPriorityMenu(null);
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  const loadLeads = async () => {
    setLoading(true);
    try {
      const response = await fetchWithAuth('/api/contact');
      const data = await response.json();
      setLeads(data);
    } catch (error) {
      console.error('Error cargando leads:', error);
    }
    setLoading(false);
  };

  useEffect(() => { loadLeads(); }, []);

  // Filtrado y ordenamiento
  const filteredLeads = leads
    .filter(lead => {
      const matchesSearch = !search || 
        (lead.name || '').toLowerCase().includes(search.toLowerCase()) ||
        (lead.email || '').toLowerCase().includes(search.toLowerCase()) ||
        (lead.phone || '').toLowerCase().includes(search.toLowerCase()) ||
        (lead.message || '').toLowerCase().includes(search.toLowerCase());
      const matchesType = filterType === 'all' || lead.property_type === filterType;
      const matchesStatus = filterStatus === 'all' || (lead.status || 'pendiente') === filterStatus;
      
      let matchesDate = true;
      if (dateFrom || dateTo) {
        const leadDate = new Date(lead.timestamp).toISOString().split('T')[0];
        if (dateFrom && leadDate < dateFrom) matchesDate = false;
        if (dateTo && leadDate > dateTo) matchesDate = false;
      }
      
      return matchesSearch && matchesType && matchesStatus && matchesDate;
    })
    .sort((a, b) => {
      if (sortBy === 'newest') return new Date(b.timestamp) - new Date(a.timestamp);
      if (sortBy === 'oldest') return new Date(a.timestamp) - new Date(b.timestamp);
      if (sortBy === 'priority') {
        const order = { alta: 0, media: 1, baja: 2 };
        return (order[a.priority] || 1) - (order[b.priority] || 1);
      }
      return 0;
    });

  const updateStatus = async (leadId, newStatus) => {
    try {
      await fetchWithAuth(`/api/contact/${leadId}/status`, {
        method: 'PUT',
        body: JSON.stringify({ status: newStatus })
      });
      setLeads(prev => prev.map(l => l.id === leadId ? { ...l, status: newStatus, last_contact_date: new Date().toISOString() } : l));
      setShowStatusMenu(null);
    } catch (error) {
      console.error('Error actualizando estado:', error);
    }
  };

  const updatePriority = async (leadId, newPriority) => {
    try {
      await fetchWithAuth(`/api/contact/${leadId}/priority`, {
        method: 'PUT',
        body: JSON.stringify({ priority: newPriority })
      });
      setLeads(prev => prev.map(l => l.id === leadId ? { ...l, priority: newPriority } : l));
      setShowPriorityMenu(null);
    } catch (error) {
      console.error('Error actualizando prioridad:', error);
    }
  };

  const addNote = async (leadId) => {
    if (!noteText.trim()) return;
    try {
      const response = await fetchWithAuth(`/api/contact/${leadId}/notes`, {
        method: 'POST',
        body: JSON.stringify({ text: noteText })
      });
      const newNote = await response.json();
      setLeads(prev => prev.map(l => l.id === leadId ? { ...l, notes: [...(l.notes || []), newNote] } : l));
      setNoteText('');
    } catch (error) {
      console.error('Error agregando nota:', error);
    }
  };

  const deleteNote = async (leadId, noteId) => {
    try {
      await fetchWithAuth(`/api/contact/${leadId}/notes/${noteId}`, { method: 'DELETE' });
      setLeads(prev => prev.map(l => l.id === leadId ? { ...l, notes: (l.notes || []).filter(n => n.id !== noteId) } : l));
    } catch (error) {
      console.error('Error eliminando nota:', error);
    }
  };

  const exportCSV = () => {
    const headers = ['Nombre', 'Email', 'Telefono', 'Tipo', 'Estado', 'Prioridad', 'Mensaje', 'Fecha'];
    const rows = filteredLeads.map(l => [
      l.name, l.email, l.phone || '', l.property_type || '', l.status || 'pendiente',
      l.priority || 'media', `"${(l.message || '').replace(/"/g, '""')}"`,
      new Date(l.timestamp).toLocaleDateString('es-PY')
    ]);
    const csv = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `zenex_leads_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  const getStatusInfo = (status) => STATUSES.find(s => s.value === status) || STATUSES[0];
  const getPriorityInfo = (priority) => PRIORITIES.find(p => p.value === priority) || PRIORITIES[1];

  const getSourceLabel = (type) => {
    const labels = { residencial: 'Residencial', comercial: 'Comercial', industrial: 'Industrial', newsletter: 'Newsletter', calculadora: 'Calculadora' };
    return labels[type] || type;
  };
  const getSourceColor = (type) => {
    const colors = { residencial: 'bg-blue-500/20 text-blue-400', comercial: 'bg-purple-500/20 text-purple-400', industrial: 'bg-orange-500/20 text-orange-400', newsletter: 'bg-emerald-500/20 text-emerald-400', calculadora: 'bg-yellow-500/20 text-yellow-400' };
    return colors[type] || 'bg-zinc-500/20 text-zinc-400';
  };

  // Pipeline stats
  const pipeline = STATUSES.map(s => ({
    ...s,
    count: leads.filter(l => (l.status || 'pendiente') === s.value).length
  }));

  return (
    <div className="space-y-6" data-testid="leads-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold text-white flex items-center gap-3">
            <Users className="text-[#90EE90]" /> Clientes Interesados
          </h1>
          <p className="text-zinc-400 mt-1">
            Consultas recibidas desde el formulario de contacto o desde la calculadora solar en linea
          </p>
        </div>
        <div className="flex gap-2">
          <button onClick={loadLeads} className="flex items-center gap-2 px-4 py-2 bg-zinc-800 hover:bg-zinc-700 text-white rounded-lg transition-colors" data-testid="refresh-leads">
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} /> Actualizar
          </button>
          <button onClick={exportCSV} className="flex items-center gap-2 px-4 py-2 bg-[#14532D] hover:bg-[#166534] text-white rounded-lg transition-colors" data-testid="export-csv">
            <Download size={16} /> Exportar CSV
          </button>
        </div>
      </div>

      {/* Pipeline - Vista de embudo */}
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2" data-testid="pipeline-stats">
        {pipeline.map(s => (
          <button
            key={s.value}
            onClick={() => setFilterStatus(filterStatus === s.value ? 'all' : s.value)}
            className={`rounded-xl p-3 text-left transition-all border ${
              filterStatus === s.value ? 'border-white/30 scale-[1.02]' : 'border-zinc-700/50 hover:border-zinc-600'
            } bg-zinc-800/50`}
          >
            <div className="flex items-center gap-1.5 mb-1">
              <div className={`w-2 h-2 rounded-full ${s.dot}`}></div>
              <span className="text-[11px] text-zinc-400 truncate">{s.label}</span>
            </div>
            <span className="text-xl font-bold text-white">{s.count}</span>
          </button>
        ))}
      </div>

      {/* Source Stats */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
        <div className="bg-zinc-800/50 border border-zinc-700 rounded-xl p-3">
          <p className="text-zinc-400 text-xs">Total</p>
          <p className="text-2xl font-bold text-white mt-1">{leads.length}</p>
        </div>
        {SOURCE_TYPES.filter(s => s.value !== 'all').map(s => (
          <div key={s.value} className="bg-zinc-800/50 border border-zinc-700 rounded-xl p-3">
            <p className="text-zinc-400 text-xs">{s.label}</p>
            <p className="text-2xl font-bold text-white mt-1">{leads.filter(l => l.property_type === s.value).length}</p>
          </div>
        ))}
      </div>

      {/* Filters Bar */}
      <div className="bg-zinc-800/30 border border-zinc-700/50 rounded-xl p-4 space-y-3">
        <div className="flex items-center gap-2 text-zinc-400 text-sm mb-2">
          <Filter size={14} /> Filtros
        </div>
        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" size={18} />
          <input
            type="text"
            placeholder="Buscar por nombre, email, telefono o mensaje..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 bg-zinc-900/80 border border-zinc-700 rounded-lg text-white placeholder-zinc-500 focus:border-[#90EE90]/50 focus:outline-none"
            data-testid="search-leads"
          />
        </div>
        
        <div className="flex flex-wrap gap-3 items-end">
          {/* Source filter */}
          <div className="flex flex-wrap gap-1.5">
            {SOURCE_TYPES.map(type => (
              <button
                key={type.value}
                onClick={() => setFilterType(type.value)}
                className={`px-3 py-1.5 rounded-lg text-sm transition-colors ${
                  filterType === type.value
                    ? (type.color || 'bg-[#14532D]') + ' text-white'
                    : 'bg-zinc-800 text-zinc-400 hover:text-white'
                }`}
                data-testid={`filter-${type.value}`}
              >
                {type.label}
              </button>
            ))}
          </div>
          
          <div className="h-6 w-px bg-zinc-700 hidden md:block"></div>
          
          {/* Date filters */}
          <div className="flex items-center gap-2">
            <Calendar size={14} className="text-zinc-500" />
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="px-3 py-1.5 bg-zinc-900 border border-zinc-700 rounded-lg text-white text-sm focus:border-[#90EE90]/50 focus:outline-none"
              data-testid="date-from"
            />
            <span className="text-zinc-500 text-sm">a</span>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className="px-3 py-1.5 bg-zinc-900 border border-zinc-700 rounded-lg text-white text-sm focus:border-[#90EE90]/50 focus:outline-none"
              data-testid="date-to"
            />
            {(dateFrom || dateTo) && (
              <button onClick={() => { setDateFrom(''); setDateTo(''); }} className="text-zinc-500 hover:text-white">
                <X size={14} />
              </button>
            )}
          </div>

          <div className="h-6 w-px bg-zinc-700 hidden md:block"></div>
          
          {/* Sort */}
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="px-3 py-1.5 bg-zinc-900 border border-zinc-700 rounded-lg text-white text-sm focus:border-[#90EE90]/50 focus:outline-none"
            data-testid="sort-leads"
          >
            <option value="newest">Mas reciente</option>
            <option value="oldest">Mas antiguo</option>
            <option value="priority">Por prioridad</option>
          </select>
        </div>
      </div>

      {/* Results count */}
      <p className="text-zinc-500 text-sm">{filteredLeads.length} de {leads.length} leads</p>

      {/* Leads List */}
      <div className="space-y-3">
        {loading ? (
          <div className="text-center py-12 text-zinc-400">Cargando...</div>
        ) : filteredLeads.length === 0 ? (
          <div className="text-center py-12 text-zinc-500">No se encontraron leads con estos filtros</div>
        ) : (
          filteredLeads.map(lead => {
            const statusInfo = getStatusInfo(lead.status || 'pendiente');
            const priorityInfo = getPriorityInfo(lead.priority || 'media');
            const isExpanded = expandedLead === lead.id;

            return (
              <div
                key={lead.id}
                className={`bg-zinc-800/50 border rounded-xl transition-all relative ${
                  isExpanded ? 'border-[#90EE90]/30' : 'border-zinc-700/50 hover:border-zinc-600'
                }`}
                style={{ zIndex: (showStatusMenu === lead.id || showPriorityMenu === lead.id) ? 40 : 1 }}
                data-testid={`lead-${lead.id}`}
              >
                {/* Lead Header */}
                <div className="p-4 flex flex-col md:flex-row md:items-center gap-3">
                  {/* Name & badges */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="text-lg font-semibold text-white truncate">{lead.name}</h3>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getSourceColor(lead.property_type)}`}>
                        {getSourceLabel(lead.property_type)}
                      </span>
                    </div>
                    <div className="flex items-center gap-4 mt-1 text-sm text-zinc-400">
                      {lead.email && <span className="flex items-center gap-1"><Mail size={12} />{lead.email}</span>}
                      {lead.phone && <span className="flex items-center gap-1"><Phone size={12} />{lead.phone}</span>}
                      <span className="flex items-center gap-1">
                        <Calendar size={12} />
                        {new Date(lead.timestamp).toLocaleDateString('es-PY', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                      </span>
                    </div>
                  </div>

                  {/* Status & Priority controls */}
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {/* Priority selector */}
                    <div className="relative">
                      <button
                        onClick={(e) => { e.stopPropagation(); setShowPriorityMenu(showPriorityMenu === lead.id ? null : lead.id); setShowStatusMenu(null); }}
                        className={`px-2.5 py-1 rounded-lg text-xs font-bold ${priorityInfo.bg} ${priorityInfo.color}`}
                        data-testid={`priority-btn-${lead.id}`}
                      >
                        {priorityInfo.icon} {priorityInfo.label}
                      </button>
                      {showPriorityMenu === lead.id && (
                        <div className="absolute right-0 top-full mt-1 bg-zinc-900 border border-zinc-700 rounded-lg shadow-xl z-50 py-1 min-w-[120px]">
                          {PRIORITIES.map(p => (
                            <button key={p.value} onClick={() => updatePriority(lead.id, p.value)}
                              className={`w-full text-left px-3 py-1.5 text-sm hover:bg-zinc-800 ${p.color}`}>
                              {p.icon} {p.label}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Status selector */}
                    <div className="relative">
                      <button
                        onClick={(e) => { e.stopPropagation(); setShowStatusMenu(showStatusMenu === lead.id ? null : lead.id); setShowPriorityMenu(null); }}
                        className={`px-3 py-1 rounded-lg text-xs font-medium flex items-center gap-1 ${statusInfo.color}`}
                        data-testid={`status-btn-${lead.id}`}
                      >
                        <div className={`w-1.5 h-1.5 rounded-full ${statusInfo.dot}`}></div>
                        {statusInfo.label}
                        <ChevronDown size={12} />
                      </button>
                      {showStatusMenu === lead.id && (
                        <div className="absolute right-0 top-full mt-1 bg-zinc-900 border border-zinc-700 rounded-lg shadow-xl z-50 py-1 min-w-[180px]">
                          {STATUSES.map(s => (
                            <button key={s.value} onClick={() => updateStatus(lead.id, s.value)}
                              className="w-full text-left px-3 py-1.5 text-sm hover:bg-zinc-800 text-zinc-300 flex items-center gap-2">
                              <div className={`w-2 h-2 rounded-full ${s.dot}`}></div>
                              {s.label}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Quick actions */}
                    {lead.phone && (
                      <a href={`https://wa.me/${(lead.phone || '').replace(/[^0-9]/g, '')}`} target="_blank" rel="noreferrer"
                        className="px-3 py-1 bg-[#25D366]/20 text-[#25D366] rounded-lg text-xs font-medium hover:bg-[#25D366]/30 transition-colors"
                        data-testid={`whatsapp-${lead.id}`}>
                        WhatsApp
                      </a>
                    )}

                    {/* Expand/Collapse */}
                    <button onClick={() => setExpandedLead(isExpanded ? null : lead.id)}
                      className="p-1.5 rounded-lg text-zinc-500 hover:text-white hover:bg-zinc-700 transition-colors">
                      {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                    </button>
                  </div>
                </div>

                {/* Expanded Content */}
                {isExpanded && (
                  <div className="border-t border-zinc-700/50 p-4 space-y-4 bg-zinc-900/30">
                    {/* Message */}
                    <div className="bg-zinc-800/60 rounded-lg p-3 border border-zinc-700/50">
                      <div className="flex items-center gap-2 text-zinc-400 text-xs mb-2">
                        <MessageSquare size={14} />
                        {lead.property_type === 'calculadora' ? 'Datos de la Calculadora' : 'Mensaje'}
                      </div>
                      <p className="text-zinc-300 text-sm">{lead.message}</p>
                    </div>

                    {/* Calculator Data */}
                    {lead.calculator_data && (
                      <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                        {lead.calculator_data.address && (
                          <div className="bg-zinc-800/60 rounded-lg p-3 border border-zinc-700/50">
                            <p className="text-zinc-500 text-xs">Direccion</p>
                            <p className="text-zinc-200 text-sm font-medium">{lead.calculator_data.address}</p>
                          </div>
                        )}
                        {lead.calculator_data.monthly_kwh && (
                          <div className="bg-zinc-800/60 rounded-lg p-3 border border-zinc-700/50">
                            <p className="text-zinc-500 text-xs">Consumo</p>
                            <p className="text-zinc-200 text-sm font-medium">{lead.calculator_data.monthly_kwh} kWh/mes</p>
                          </div>
                        )}
                        {lead.calculator_data.recommended_panels && (
                          <div className="bg-zinc-800/60 rounded-lg p-3 border border-zinc-700/50">
                            <p className="text-zinc-500 text-xs">Paneles</p>
                            <p className="text-zinc-200 text-sm font-medium">{lead.calculator_data.recommended_panels}</p>
                          </div>
                        )}
                        {lead.calculator_data.system_capacity_kw && (
                          <div className="bg-zinc-800/60 rounded-lg p-3 border border-zinc-700/50">
                            <p className="text-zinc-500 text-xs">Capacidad</p>
                            <p className="text-zinc-200 text-sm font-medium">{lead.calculator_data.system_capacity_kw} kW</p>
                          </div>
                        )}
                        {lead.calculator_data.roi_years && (
                          <div className="bg-zinc-800/60 rounded-lg p-3 border border-zinc-700/50">
                            <p className="text-zinc-500 text-xs">ROI</p>
                            <p className="text-yellow-400 text-sm font-medium">{lead.calculator_data.roi_years} anos</p>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Timeline info */}
                    {lead.last_contact_date && (
                      <div className="flex items-center gap-2 text-xs text-zinc-500">
                        <Clock size={12} />
                        Ultimo contacto: {new Date(lead.last_contact_date).toLocaleDateString('es-PY', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                      </div>
                    )}

                    {/* Notes Section */}
                    <div>
                      <div className="flex items-center gap-2 text-sm text-zinc-400 mb-2">
                        <StickyNote size={14} /> Notas de seguimiento ({(lead.notes || []).length})
                      </div>
                      
                      {(lead.notes || []).length > 0 && (
                        <div className="space-y-2 mb-3">
                          {(lead.notes || []).map(note => (
                            <div key={note.id} className="flex items-start gap-2 bg-zinc-800/60 rounded-lg p-2.5 border border-zinc-700/30 group">
                              <div className="flex-1">
                                <p className="text-zinc-300 text-sm">{note.text}</p>
                                <p className="text-zinc-600 text-xs mt-1">
                                  {new Date(note.timestamp).toLocaleDateString('es-PY', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                                </p>
                              </div>
                              <button onClick={() => deleteNote(lead.id, note.id)}
                                className="opacity-0 group-hover:opacity-100 text-zinc-600 hover:text-red-400 transition-all p-1">
                                <X size={12} />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Add note */}
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={expandedLead === lead.id ? noteText : ''}
                          onChange={(e) => setNoteText(e.target.value)}
                          onKeyDown={(e) => e.key === 'Enter' && addNote(lead.id)}
                          placeholder="Agregar nota de seguimiento..."
                          className="flex-1 px-3 py-2 bg-zinc-900 border border-zinc-700 rounded-lg text-white text-sm placeholder-zinc-600 focus:border-[#90EE90]/50 focus:outline-none"
                          data-testid={`note-input-${lead.id}`}
                        />
                        <button onClick={() => addNote(lead.id)}
                          className="px-3 py-2 bg-[#14532D] hover:bg-[#166534] text-white rounded-lg transition-colors"
                          data-testid={`add-note-${lead.id}`}>
                          <Plus size={16} />
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default LeadsPage;
